import axios from 'axios';
import type { IProvider } from '@web3auth/modal';
import RPC from '../auth/viemRPC';
import { SERVER_URL } from '../config';

export const WITHDRAW_API_BASE_URL = SERVER_URL;
export const WITHDRAW_PROGRESS_KEY = 'withdrawInProgress';
export const PENDING_GUARDARIAN_WITHDRAW_KEY = 'pendingGuardarianWithdraw';
export const PENDING_OFF_RAMP_KEY = 'pendingOffRamp';
export const GUARDARIAN_RETURN_INTENT_KEY = 'guardarianReturnIntent';

export type GuardarianReturnIntent = 'deposit' | 'withdraw';

export type PendingGuardarianWithdraw = {
  requestId?: string;
  txId?: string;
  ethAmount?: string;
  depositAddress?: string;
  redirectUrl?: string;
  instructionAcknowledgedAt?: number;
  wrongButtonReminderShownAt?: number;
  createdAt?: number;
  source?: 'withdraw' | 'wallet_send';
};

export type QueueGuardarianWithdrawalResult =
  | {
      ok: true;
      requestId?: string;
      walletAddress: string;
      alreadyPending: boolean;
    }
  | {
      ok: false;
      code:
        | 'NO_PROVIDER'
        | 'INVALID_AMOUNT'
        | 'INVALID_DEPOSIT_ADDRESS'
        | 'NO_WALLET'
        | 'REQUEST_FAILED';
      message: string;
    };

const isValidEthAddress = (value: string) => /^0x[a-fA-F0-9]{40}$/.test(value);

export const setWithdrawInProgress = (value: boolean) => {
  try {
    localStorage.setItem(WITHDRAW_PROGRESS_KEY, value ? '1' : '0');
  } catch (error) {
    console.warn('Unable to persist withdraw progress flag:', error);
  }
};

export const getWithdrawInProgress = () => {
  try {
    return localStorage.getItem(WITHDRAW_PROGRESS_KEY) === '1';
  } catch {
    return false;
  }
};

export const readPendingGuardarianWithdraw = (): PendingGuardarianWithdraw | null => {
  try {
    const raw = localStorage.getItem(PENDING_GUARDARIAN_WITHDRAW_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as PendingGuardarianWithdraw;
  } catch (error) {
    console.warn('Unable to restore pending Guardarian withdraw state:', error);
    return null;
  }
};

export const persistPendingGuardarianWithdraw = (
  payload: Omit<PendingGuardarianWithdraw, 'createdAt'>
) => {
  try {
    const existing = readPendingGuardarianWithdraw() || {};
    localStorage.setItem(
      PENDING_GUARDARIAN_WITHDRAW_KEY,
      JSON.stringify({
        ...existing,
        ...payload,
        createdAt: Date.now(),
      })
    );
  } catch (error) {
    console.warn('Unable to persist pending Guardarian withdraw state:', error);
  }
};

export const markGuardarianInstructionAcknowledged = () => {
  const existing = readPendingGuardarianWithdraw() || {};
  persistPendingGuardarianWithdraw({
    ...existing,
    instructionAcknowledgedAt: Date.now(),
  });
};

export const markWrongButtonReminderShown = () => {
  const existing = readPendingGuardarianWithdraw() || {};
  persistPendingGuardarianWithdraw({
    ...existing,
    wrongButtonReminderShownAt: Date.now(),
  });
};

export const clearPendingGuardarianWithdraw = () => {
  try {
    localStorage.removeItem(PENDING_GUARDARIAN_WITHDRAW_KEY);
  } catch (error) {
    console.warn('Unable to clear pending Guardarian withdraw state:', error);
  }
};

export const persistPendingOffRamp = (ethAmount: string) => {
  try {
    localStorage.setItem(
      PENDING_OFF_RAMP_KEY,
      JSON.stringify({ ethAmount, createdAt: Date.now() })
    );
  } catch (error) {
    console.error('Error persisting pending off-ramp state:', error);
  }
};

export const clearPendingOffRamp = () => {
  try {
    localStorage.removeItem(PENDING_OFF_RAMP_KEY);
  } catch {
    // no-op
  }
};

export const setGuardarianReturnIntent = (intent: GuardarianReturnIntent) => {
  try {
    localStorage.setItem(GUARDARIAN_RETURN_INTENT_KEY, intent);
  } catch (error) {
    console.warn('Unable to persist Guardarian return intent:', error);
  }
};

export const getGuardarianReturnIntent = (): GuardarianReturnIntent | null => {
  try {
    const value = localStorage.getItem(GUARDARIAN_RETURN_INTENT_KEY);
    if (value === 'deposit' || value === 'withdraw') {
      return value;
    }
    return null;
  } catch {
    return null;
  }
};

export const clearGuardarianReturnIntent = () => {
  try {
    localStorage.removeItem(GUARDARIAN_RETURN_INTENT_KEY);
  } catch (error) {
    console.warn('Unable to clear Guardarian return intent:', error);
  }
};

export const queueGuardarianWithdrawal = async ({
  provider,
  amountEth,
  depositAddress,
}: {
  provider: IProvider | null;
  amountEth: string;
  depositAddress: string;
}): Promise<QueueGuardarianWithdrawalResult> => {
  if (!provider) {
    return {
      ok: false,
      code: 'NO_PROVIDER',
      message: 'Wallet connection not found. Please connect your wallet and try again.',
    };
  }

  const ethAmountNum = Number(amountEth);
  if (!isFinite(ethAmountNum) || ethAmountNum <= 0) {
    return {
      ok: false,
      code: 'INVALID_AMOUNT',
      message: 'Please enter an amount greater than 0.',
    };
  }

  if (!isValidEthAddress(depositAddress)) {
    return {
      ok: false,
      code: 'INVALID_DEPOSIT_ADDRESS',
      message: 'Guardarian did not provide a valid deposit address yet.',
    };
  }

  let walletAddress = '';

  try {
    const rpc = new RPC(provider);
    const addresses = await rpc.getAccounts();

    if (!Array.isArray(addresses) || addresses.length === 0 || !addresses[0]) {
      return {
        ok: false,
        code: 'NO_WALLET',
        message: 'No wallet address found. Please connect your wallet and try again.',
      };
    }

    walletAddress = String(addresses[0]);

    const response = await axios.post(`${WITHDRAW_API_BASE_URL}/qachewithdraw`, {
      network: 'BASE_MAINNET',
      walletAddress,
      value: ethAmountNum,
      guardarianDepositAddress: depositAddress,
      asset: 'USDC',
    });

    return {
      ok: true,
      requestId: response.data?.pendingTransactionId,
      walletAddress,
      alreadyPending: false,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return {
        ok: true,
        walletAddress,
        alreadyPending: true,
      };
    }

    const responseMessage =
      axios.isAxiosError(error)
        ? error.response?.data?.message || error.response?.data?.error
        : '';

    return {
      ok: false,
      code: 'REQUEST_FAILED',
      message:
        responseMessage || 'There was an error processing your withdrawal. Please try again later.',
    };
  }
};
